/**
 * @file Editable payment form, shared by the payment page and the create dialog.
 * Its state is pushed to the parent through `updateParentState`, which owns the payload.
 */
import * as React from "react";
import Component from "../../AbstractComponent";
import ChargeClassContainer from "../../../Entity/Entities/Charge/ChargeClass/ChargeClassContainer";
import { PAYMENT_ROUTE } from "../../../Entity/Entities/Charge/PaymentRoute";
import {
  getBaseBankAccountDefaults,
  getBaseBankAccountFields,
  getBaseBankAccountPresentation,
  getPaymentRouteIdForPayment,
} from "../../../Entity/Entities/Payment/BaseBankAccount";
import Searchbar from "../Searchbar/Searchbar";
import { DatePickerComponent } from "../DatePicker/DatePickerComponent";
import { PersonInterface } from "../../../Entity/Entities/Person/PersonInterface";
import { CompanyInterface } from "../../../Entity/Entities/Company/CompanyInterface";
import Formatter from "../../../Localization/Formatter";
import { BankAccountInterface } from "../../../Entity/Entities/BankAccount/BankAccountInterface";
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Select from "../../Partials/Material/Select";
import MenuItem from '@material-ui/core/MenuItem';
import TextField from "../../Partials/Material/TextField";
import Radio from '@material-ui/core/Radio';
import RadioGroup from '@material-ui/core/RadioGroup';
import { PaymentTypeInterface } from "../../../Entity/Entities/Payment/PaymentType/PaymentTypeInterface";
import PaymentTypeContainer from "../../../Entity/Entities/Payment/PaymentType/PaymentTypeContainer";
import PaymentClassContainer from "../../../Entity/Entities/Payment/PaymentClass/PaymentClassContainer";
import { CoachNewCounterpartyDialog } from "../../Partials/Dialog/CoachNewCounterpartyDialog";

export default class Charge2DetailsEditable extends Component {
	public state = {
		amountForPayment: "",
		currencyIdForPayment: this.props.app?.settings?.user?.currency?.getCurrencyId(),
		firstDateOfStatementPeriodForPayment: new Date(),
		dateOfThePayment: new Date(),
		typeIdForPayment: String(PaymentTypeContainer.pickIdFromLabel("Bank")),
		agencyBankAccountIdForPayment: "",
		baseBankAccountForPayment: "",
		baseBankAccountNameForPayment: "",
    agencyBankAccounts: [],
    paymentClassId: PaymentClassContainer.pickIdFromLabel("Receivable"),
    selectedCounterpartyForPayment: null,
    descriptionForPayment: "",
		descriptionForPaymentIsManuallyChanged: false,
    openCoachCreateNewCounterparty: false
  };

  constructor(public props) {
    super(props);
  }

  // Derived per call, never stored: it reads `this.props.payment` and the Redux bank-account
  // list, which both change without this component's own state being rewritten.
  public getBaseAccountPresentation = () => getBaseBankAccountPresentation({
    paymentRouteId: getPaymentRouteIdForPayment(this.props.payment, this.getAgency()?.id),
    agencyId: this.getAgency()?.id,
    ownerCompanyId: this.props.payment?.baseAccountCompanyId ?? null,
    agencyBankAccountCount: this.getAgencyBankAccounts().length,
  });

  public componentWillMount() {
    const agencyBankAccounts: BankAccountInterface[] = this.getAgencyBankAccounts();

    const defaultBankAccount: BankAccountInterface = agencyBankAccounts.find(bankAccount => {
      return bankAccount.isDefault === true;
    });

    let agencyBankAccountId;

    if (!!defaultBankAccount) {
      agencyBankAccountId = !!defaultBankAccount ? defaultBankAccount.id : null;
    } else {
      agencyBankAccountId = !!agencyBankAccounts[0] ? agencyBankAccounts[0].id : null;
    }

    this.setState({
      agencyBankAccountIdForPayment: agencyBankAccountId
    });

    if (this.props.payment) {
      this.setState({
        amountForPayment: Formatter.simpleFormatNumber(this.props.payment?.amount),
        paymentClassId: String(this.props.payment?.paymentClass?.id),
        currencyIdForPayment: this.props.payment?.currency?.getCurrencyId(),
        firstDateOfStatementPeriodForPayment: this.props.payment?.firstDateOfStatementPeriod,
        dateOfThePayment: this.props.payment?.dateOfThePayment,
        typeIdForPayment: String(this.props.payment?.paymentType?.id),
        selectedCounterpartyForPayment: this.props.payment?.counterparty,
        agencyBankAccountIdForPayment: this.props.payment?.agencyBankAccount,
        baseBankAccountForPayment: this.props.payment?.baseBankAccount || "",
        baseBankAccountNameForPayment: this.props.payment?.baseBankAccountName || "",
        descriptionForPayment: this.props.payment?.description,
        descriptionForPaymentIsManuallyChanged: false,
      });
    } else {
      const baseAccountDefaults = getBaseBankAccountDefaults({
        paymentRouteId: PAYMENT_ROUTE.AGENCY,
        usesSelector: this.getBaseAccountPresentation().usesSelector,
        baseAccountCompany: this.getAgency(),
        transactionIban: "",
      });

      this.setState({
        baseBankAccountForPayment: baseAccountDefaults.baseBankAccount,
        baseBankAccountNameForPayment: baseAccountDefaults.baseBankAccountName,
      });
    }

    if (this.props.charge) {
      const chargeClassId: number = this.props.charge?.chargeClass?.id;
      
      this.setState({
        amountForPayment: Formatter.simpleFormatNumber(this.props.charge?.amountRemaining),
        paymentClassId: String(chargeClassId),
        currencyIdForPayment: this.props.charge?.currency?.getCurrencyId(),
        firstDateOfStatementPeriodForPayment: new Date(),
        typeIdForPayment: String(PaymentTypeContainer.pickIdFromLabel("Bank")),
        selectedCounterpartyForPayment: this.props.charge?.counterparty,
        descriptionForPayment: "",
        descriptionForPaymentIsManuallyChanged: false,
      });
    }
	}

  public updateParentState = (callback?) => {
    if (typeof this.props.updateState === "function") {
      // Pushed as one object so each parent reads the base-account rule rather than re-deriving it.
      const baseAccountFields = getBaseBankAccountFields({
        isBankType: Number(this.state.typeIdForPayment) === PaymentTypeContainer.pickIdFromLabel("Bank"),
        presentation: this.getBaseAccountPresentation(),
        // The dropdown's unselected state is `""` here, not null, so it is normalized.
        selectedBankAccountId: this.state.agencyBankAccountIdForPayment
          ? Number(this.state.agencyBankAccountIdForPayment)
          : null,
        baseBankAccount: this.state.baseBankAccountForPayment,
        baseBankAccountName: this.state.baseBankAccountNameForPayment,
      });

      this.props.updateState.call(this, {...this.state, baseAccountFields, paymentDataChanged: true}, callback);
    }
  }

  public changeBaseBankAccountForPayment = (event): void => {
    const baseBankAccount: string = event.target.value;
    this.setState({
      baseBankAccountForPayment: baseBankAccount,
      hasModifiedData: true,
    }, this.updateParentState);
  }

  public changeBaseBankAccountNameForPayment = (event): void => {
    const baseBankAccountName: string = event.target.value;
    this.setState({
      baseBankAccountNameForPayment: baseBankAccountName,
      hasModifiedData: true,
    }, this.updateParentState);
  }

  public selectDate = (date: Date, identifier: string): void => {
    this.setState({
      [identifier]: date, // todo fix hardcoded value
      hasModifiedData: true
    }, this.updateParentState);
  }

  public changeAmountForPayment = (event): void => {
    let amount: string = event.target.value;
    amount = this.formatter.formatSigns(amount);

    this.setState({
      amountForPayment: amount,
      hasModifiedData: true
    }, this.updateParentState);
  }

  public changeCurrencyIdForPayment = (event): void => {
    let currency: number = event.target.value;

    this.setState({
      changeCurrencyIdForPayment: currency,
      hasModifiedData: true
    }, this.updateParentState);
  }

  public selectCounterpartyForPayment = (entity: CompanyInterface | PersonInterface): void => {
    this.setState({
      selectedCounterpartyForPayment: entity,
      hasModifiedData: true
    }, this.updateParentState);
  }

  public clearCounterpartyForPayment = (): void => {
    this.setState({
      selectedCounterpartyForPayment: null,
      hasModifiedData: true
    }, this.updateParentState);
  }

  public changeTypeIdForPayment = (event): void => {
    const type = String(event.target.value);

    this.setState({
      typeIdForPayment: type,
      hasModifiedData: true,
    }, this.updateParentState);
  }

  public updateAgencyBankAccountIdForPayment = (event): void => {
    const bankAccountId: number = event.target.value;

    if (bankAccountId) {
      this.setState({
        agencyBankAccountIdForPayment: bankAccountId,
        hasModifiedData: true
      }, this.updateParentState);
    }
  }


  public changePaymentClassId = (event): void => {
    const paymentClassId = String(event.target.value);

    this.setState({
      paymentClassId: paymentClassId,
      hasModifiedData: true
    }, this.updateParentState);
  }

  public changeDescriptionForPayment = (event): void => {
    const description: string = event.target.value;
    this.setState({
      descriptionForPayment: description,
      hasModifiedData: true,
      descriptionForPaymentIsManuallyChanged: true,
    }, this.updateParentState);
  }

  public renderCounterpartyField = (classId) => {
    return (
      <div className="search-bar-container" style={{/*margin: "-20px 0 -10px 0"*/}}>
        <Searchbar
          {...this.props}
          id="selectedCounterpartyForPayment"
          className={"scroll-to-counterpartyId"}
          // floatingLabelText={labelText}
          floatingLabelText={Number(classId) == PaymentClassContainer.pickIdFromLabel("Receivable") ? this.trans("section/charge-field/from-label") : this.trans("section/charge-field/to-label")}
          allResults={true}
          objectField={true}
          onChange={this.selectCounterpartyForPayment}
          onClear={this.clearCounterpartyForPayment}
          selectedEntity={this.state.selectedCounterpartyForPayment}
          searchParameter={"contactsSearch"}
          error={this.getErrorFor("counterpartyId")}
          showClickThroughLink={!this.props.dialogPresentation}
          searchText={this.state.selectedCounterpartyForPayment ? `${this.state.selectedCounterpartyForPayment.nameOfEntity}, ${this.state.selectedCounterpartyForPayment.displayName}` : null}
          styleInModal={"pick-object-modal"}
          createButton={"dropdown-button/newcounterparty"}
          openCoachCreateNewEntity={() => {
            this.setState({
              openCoachCreateNewCounterparty: true,
              selectedCounterparty: null
            })}
          }
        />
      </div>
    );
  }

  public renderBankAccountField = (classId) => {
    if (!this.getBaseAccountPresentation().usesSelector) {
      return (
        <>
          <TextField
            autoComplete="off"
            id="baseBankAccountForPayment"
            name="baseBankAccountForPayment"
            className="form-element full-size scroll-to-baseBankAccountForPayment"
            type="text"
            label={this.trans("section/payment-field/bankaccount-label")}
            value={this.state.baseBankAccountForPayment}
            onChange={this.changeBaseBankAccountForPayment}
            variant="standard"
            error={!!this.getErrorFor("baseBankAccountForPayment")}
          />
          {!!this.getErrorFor("baseBankAccountForPayment") && <div className="component-error-text">{this.getErrorFor("baseBankAccountForPayment")}</div>}
          <TextField
            autoComplete="off"
            id="baseBankAccountNameForPayment"
            name="baseBankAccountNameForPayment"
            className="form-element full-size scroll-to-baseBankAccountNameForPayment"
            type="text"
            label={this.trans("section/payment-field/bankaccountname-label")}
            value={this.state.baseBankAccountNameForPayment}
            onChange={this.changeBaseBankAccountNameForPayment}
            variant="standard"
            error={!!this.getErrorFor("baseBankAccountNameForPayment")}
          />
          {!!this.getErrorFor("baseBankAccountNameForPayment") && <div className="component-error-text">{this.getErrorFor("baseBankAccountNameForPayment")}</div>}
        </>
      );
    }

    return (
      <Select
        className="form-element full-size form-input bank-account-wrapper scroll-to-agencyBankAccountIdForPayment correct-label-position"
        variant="standard"
        error={this.getErrorFor("agencyBankAccountIdForPayment")}
        name="agencyBankAccountIdForPayment"
        // id="agencyBankAccountIdForPayment"
        value={!!this.state.agencyBankAccountIdForPayment ? this.state.agencyBankAccountIdForPayment : ""}
        onChange={this.updateAgencyBankAccountIdForPayment}
        htmlFor="agencyBankAccountIdForPayment"
        label={Number(classId) == ChargeClassContainer.pickIdFromLabel("Receivable") ? this.trans("section/charge-field/to-label") : this.trans("section/charge-field/from-label")}
      >
        {
          this.getAgencyBankAccounts()
            .map((bankAccount: BankAccountInterface, key: number) => {
              return (
                <MenuItem
                  key={`bankAccount-${key}`}
                  value={bankAccount.id}
                  className="filter-payment-item"
                >
                <div className="bank-account-menuitem-wrapper">
                  <div>{`${bankAccount.accountId ? `${bankAccount.accountId}` : ""} ${bankAccount.name !== "" && bankAccount.name ? ` - ${bankAccount.name}` : ""}`}</div>
                </div>
                </MenuItem>
              );
            })
        }
      </Select>
    );
  }

  public renderPayment = (classId) => {
    return (
      <div style={{display: "flex", justifyContent: "flex-start"}}>
        <div>
          <div style={{width: "420px", display: "flex", justifyContent: "flex-start", alignItems: "flex-start"}}>
            <Select
              className="form-element form-input scroll-to-chargeClassId correct-label-position"
              error={this.getErrorFor("paymentClassId")}
              style={{width: "30%", marginRight: "10px"}}
              value={Number(classId)}
              onChange={this.changePaymentClassId}
              htmlFor="paymentClassId"
              label={this.trans("section/chapaymentrge-field/class-label")}
              name="paymentClassId" 
              id="paymentClassId"
            >
              <MenuItem
                key={`paymentClassId-${PaymentClassContainer.pickIdFromLabel("Receivable")}`}
                value={PaymentClassContainer.pickIdFromLabel("Receivable")}
              >
                {this.trans("section/payment-field/classreceived-label")}
              </MenuItem>
              <MenuItem
                key={`paymentClassId-${PaymentClassContainer.pickIdFromLabel("Payable")}`}
                value={PaymentClassContainer.pickIdFromLabel("Payable")}
              >
                {this.trans("section/payment-field/classpaid-label")}
              </MenuItem>
            </Select>
          </div>

          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              width: "420px",
            }}
          >
            <div style={{display: "flex", flexDirection: "column", width: "70%", marginRight: "10px"}}>
              <TextField
                // id="аmountForPayment"
                type="text"
                value={this.state.amountForPayment}
                onChange={this.changeAmountForPayment}
                autoFocus={true}
                className="form-element scroll-to-amountForPayment"
                label={this.trans("section/payment-field/аmount-label")}
                helperText={this.getErrorFor("amountForPayment")}
                error={!!this.getErrorFor("amountForPayment")}
              />
            </div>

            <Select
              className="form-element form-input scroll-to-currencyIdForPayment"
              style={{width: "30%"}}
              error={this.getErrorFor("currencyIdForPayment")}
              value={this.state.currencyIdForPayment}
              onChange={this.changeCurrencyIdForPayment}
              htmlFor="currencyIdForPayment"
              name="currencyIdForPayment" 
              id="currencyIdForPayment"
            >
              {this.getCurrencySelectboxes()}
            </Select>
          </div>

          {
						!this.props.payment?.hasSettlementType && PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.props.payment?.paymentType?.id &&
            (
              <RadioGroup
                name="typeFilters"
                className="form-element filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
									PaymentTypeContainer.getInstances()
										.filter((paymentType: PaymentTypeInterface) => {
                      return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Settlement") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque")  
										})
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

					{
						!this.props.payment?.hasSettlementType && !(PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.props?.payment?.paymentType?.id) &&
            (
              <RadioGroup
                name="typeFilters"
                className="form-element filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
									PaymentTypeContainer.getInstances()
										.filter((paymentType: PaymentTypeInterface) => {
                      return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Settlement") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque") 
                      && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Savings_account")
 
										})
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

          {
						this.props?.payment?.hasSettlementType && !(PaymentTypeContainer.pickIdFromLabel("Savings_account") === this.props?.payment?.paymentType?.id) &&
            (
              <RadioGroup
                name="typeFilters"
                className="form-element filters-container"
                value={this.state.typeIdForPayment}
                onChange={this.changeTypeIdForPayment}
                style={{paddingLeft: "0px", width: "auto", margin: "0"}}
              >
                {
                  PaymentTypeContainer.getInstances()
                  .filter((paymentType: PaymentTypeInterface) => {
                    return paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Cheque")  
                    && paymentType.id !== PaymentTypeContainer.pickIdFromLabel("Savings_account")
                    })
                    .map((paymentType: PaymentTypeInterface, index: number) => {
                        return (
                          <FormControlLabel
                            control={<Radio color="primary" />}
                            key={`paymentTypeId-${index}`}
                            value={String(paymentType.id)}
                            label={this.trans(`payment_type_${paymentType.label}`)}
                            style={{fontSize: "16px"}}
                          />
                        );
                      })
                }
              </RadioGroup>
            )
					}

          {Number(classId) == ChargeClassContainer.pickIdFromLabel("Receivable") && (
            <>
                {this.renderCounterpartyField(classId)}
              {
                String(this.state.typeIdForPayment) === String(PaymentTypeContainer.pickIdFromLabel("Bank")) && (
                  <>
                    {this.renderBankAccountField(classId)}
                  </>
                )
              }
            </>
          )}
          
          {Number(classId) == ChargeClassContainer.pickIdFromLabel("Payable") && (
            <>
              {
                String(this.state.typeIdForPayment) === String(PaymentTypeContainer.pickIdFromLabel("Bank")) && (
                  <>
                    {this.renderBankAccountField(classId)}
                  </>
                )
              }
                {this.renderCounterpartyField(classId)}
            </>
          )}

          <DatePickerComponent
            {...this.props}
            id="dateOfThePayment"
            name="dateOfThePayment"
            className="form-element scroll-to-dateOfThePayment"
            label={this.trans("section/payment-field/date-label")}
            initialDate={this.state.dateOfThePayment}
            onChange={date => this.selectDate(date, "dateOfThePayment")}
            clearButton={true}
            error={this.getErrorFor("dateOfThePayment")}
          />

          <div style={{/*width: "420px"*/}}>
            <TextField
              autoComplete="off"
              id="descriptionForPayment"
              name="descriptionForPayment"
              className="form-element full-size scroll-to-descriptionForPayment"
              type="text"
              label={this.trans("section/payment-sub/banktransaction-field/description-label") + this.trans("field/optional")}
              value={this.state.descriptionForPayment}
              onChange={this.changeDescriptionForPayment}
              variant="standard"
              multiline={true}
              rowsMax={10}
              error={!!this.getErrorFor("descriptionForPayment")}
              helperText={this.getErrorFor("descriptionForPayment")}
            />
          </div>

          { 
            this.props.payment?.chargeIds?.length > 0 &&
            <div style={{margin: "10px 0"}}>
              <DatePickerComponent
                {...this.props}
                id="firstDateOfStatementPeriodForPayment"
                name="firstDateOfStatementPeriodForPayment"
                className="form-element scroll-to-firstDateOfStatementPeriodForPayment"
                label={this.trans("section/payment-field/forperiod-label")}
                initialDate={this.state.firstDateOfStatementPeriodForPayment}
                onChange={date => this.selectDate(date, "firstDateOfStatementPeriodForPayment")}
                clearButton={true}
                openTo={"month"}
                views={["year", "month"]}
                disableKeyboard={true}
                error={this.getErrorFor("firstDateOfStatementPeriodForPayment")}
                disabled={this.props.payment?.isClosedInStatement}
              />
            </div>
          }
        </div>
      </div>
    );
  }

  public render() {
    return (
      <div style={{position: "relative"}}>
        {this.renderPayment(this.state.paymentClassId)}

        <CoachNewCounterpartyDialog
          {...this.props}
          openCoachCreateNewCounterparty={this.state.openCoachCreateNewCounterparty}
          onCloseCounterpartyCoach={entity => {
            if(entity) {
              this.setState({
                openCoachCreateNewCounterparty: false            
              }, () => {this.selectCounterpartyForPayment(entity)})
            } else {
              this.setState({
                openCoachCreateNewCounterparty: false  
              })          
            }
          }}
        />
      </div>
    );
  }
}
